// src/features/reporting/components/analysisParts.jsx
//
// Small pieces shared by the inventory analysis pages.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, MenuItem, Skeleton, TextField, Typography } from '@mui/material';
import { format, subDays } from 'date-fns';
import InfoTip from '../../../components/common/InfoTip';

// Two-series chart colours from the Orbit ramps: primary 500 and yellowAccent 600.
// Run through the dataviz palette validator against the light (#FFFFFF) and dark
// (#111827) surfaces: lightness band, chroma, CVD and contrast all pass in both.
export const SERIES_COLORS = ['#3F68A6', '#D97706'];

export const PERIODS = [30, 90, 180, 365];

/** {startDate, endDate} as API date strings for the last `days` days. */
export const periodDates = (days) => ({
  startDate: format(subDays(new Date(), days), 'yyyy-MM-dd'),
  endDate: format(new Date(), 'yyyy-MM-dd'),
});

export function PeriodSelect({ value, onChange, periods = PERIODS }) {
  const { t } = useTranslation(['reporting']);
  return (
    <TextField
      select
      size="small"
      label={t('reporting:analysis.period')}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      sx={{ minWidth: 180 }}
    >
      {periods.map((days) => (
        <MenuItem key={days} value={days}>{t(`reporting:analysis.periods.${days}`)}</MenuItem>
      ))}
    </TextField>
  );
}

/**
 * Stat tile: label, semibold value in text ink (colour never carries the number),
 * optional caption, and an optional `info` explaining what the number means.
 */
export function StatTile({ label, value, caption, loading, info }) {
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, minWidth: 0 }}>
      <CardContent sx={{ py: '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" noWrap>{label}</Typography>
          <InfoTip title={info} />
        </Box>
        {loading
          ? <Skeleton variant="text" width="60%" height={36} />
          : <Typography variant="h4" fontWeight={600} lineHeight={1.25} noWrap title={String(value)}>{value}</Typography>}
        {caption && (
          <Typography variant="caption" color="text.secondary" noWrap display="block" title={caption}>{caption}</Typography>
        )}
      </CardContent>
    </Card>
  );
}

export function StatRow({ children }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${React.Children.count(children)}, 1fr)` }, gap: 3, mb: 4 }}>
      {children}
    </Box>
  );
}

export function FilterRow({ children }) {
  return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center', mb: 4 }}>{children}</Box>;
}

export function ChartCard({ title, description, children, actions, info }) {
  return (
    // flexShrink 0: pages are flex columns and a Card hides overflow, so it would shrink below its content
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, mb: 4, flexShrink: 0 }}>
      <CardContent sx={{ p: 5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight={600}>{title}</Typography>
              <InfoTip title={info} size={18} />
            </Box>
            {description && <Typography variant="body2" color="text.secondary">{description}</Typography>}
          </Box>
          {actions}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

/** Compact DKK for axis ticks: "48 t. kr.", "1,2 mio. kr." */
export const formatCompactDKK = (value) =>
  `${Number(value).toLocaleString('da-DK', { notation: 'compact', maximumFractionDigits: 1 })} kr.`;

export const formatPercent = (value, digits = 1) =>
  `${Number(value).toLocaleString('da-DK', { maximumFractionDigits: digits })} %`;
